import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Wraps /app/* routes (after auth). On first render, fetches the workspace
 * stats. If the workspace is empty AND the user is on a "data-driven" page,
 * we redirect them to the Configurer (Assistant onboarding).
 *
 * The user can always navigate freely to /app/configurer or /app/risques to
 * start populating the workspace; the redirect only happens once per
 * navigation away from those entry points.
 */
const ENTRY_ALLOWLIST = new Set([
  "/app/configurer",
  "/app/risques",
  "/app/admin",
  "/app/leads",
  "/app/settings",
  "/app/rh",
  "/app/financement",
  "/app/origination",
  "/app/sources-capital",
  "/app/audits",
  "/app/analyses",
  "/app/positionnement",
  "/app/controles",
  "/app/incidents",
  "/app/alertes",
  "/app/fournisseurs",
  "/app/plans",
  "/app/filtres",
  "/app/doctrine",
  "/app/trend",
  "/app/problem-gravity",
  "/app/utility-mapping",
  "/app/operational-gravity",
  "/app/chat",
  "/app/bouteille-a-la-mer",
  "/app/cercle",
  "/app/chat-interne",
]);

export default function OnboardingGate() {
  const { user } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState(null); // null = checking | object
  const [seenOnboarding, setSeenOnboarding] = useState(() => {
    try { return window.sessionStorage.getItem("noam_onboarded") === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (!user || user === false || user === null) return;
    if (seenOnboarding) return;
    api.get("/workspace/me").then((r) => setStats(r.data.stats)).catch(() => setStats({ is_empty: false }));
  }, [user, seenOnboarding]);

  // Mark onboarding as completed when user reaches /configurer
  useEffect(() => {
    if (location.pathname.startsWith("/app/configurer") || location.pathname.startsWith("/app/risques")) {
      try { window.sessionStorage.setItem("noam_onboarded", "1"); } catch {}
      setSeenOnboarding(true);
    }
  }, [location.pathname]);

  // W11 crédibilité — un utilisateur qui a déjà travaillé (noam_landing) retourne
  // à son System, jamais à l'assistant de configuration.
  let hasWorkedBefore = false;
  try { hasWorkedBefore = !!window.localStorage.getItem("noam_landing"); } catch { /* noop */ }

  // Initial route allow-list (avoid loops)
  if (
    stats &&
    stats.is_empty &&
    !seenOnboarding &&
    !hasWorkedBefore &&
    !ENTRY_ALLOWLIST.has(location.pathname) &&
    !location.pathname.startsWith("/app/risques/")
  ) {
    return <Navigate to="/app/configurer" replace state={{ from: location.pathname, onboarding: true }} />;
  }

  return <Outlet />;
}
